'use client'

import { usePathname } from 'next/navigation'
import { EllipsisVertical } from 'lucide-react'
import useAppRouter from 'app/hooks/useAppRouter'
import useAddQueryToNewLink from 'app/hooks/useAddQueryToNewLink'
import { Button } from 'ui/button'
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'ui/sheet'

const MOBILE_SHOW_ITEMS_BOTTOM_NAV = 4

const MobileNavigation = ({ navItems }: { navItems: ILeftSidebarItems[] }) => {
    const { addQueryToNewLink } = useAddQueryToNewLink()
    const pathname = usePathname()
    const { appRouter } = useAppRouter()
    const needsDrawer = navItems.length > MOBILE_SHOW_ITEMS_BOTTOM_NAV
    const bottomNavigationItems = needsDrawer ? navItems.slice(0, MOBILE_SHOW_ITEMS_BOTTOM_NAV - 1) : navItems
    const drawerItems = navItems.slice(MOBILE_SHOW_ITEMS_BOTTOM_NAV - 1)

    const onNavigationSelection = (currentItem: ILeftSidebarItems) =>
        appRouter(addQueryToNewLink(currentItem.href) || '')

    const getValueFromUrl = (url?: string) => url?.split('/').pop() || ''

    const isMoreActive = drawerItems.some(item => getValueFromUrl(pathname) === getValueFromUrl(item.href))

    const renderNavigationButton = (currentItem: ILeftSidebarItems) => (
        <Button
            type='button'
            variant={'ghost'}
            key={currentItem.href}
            value={currentItem.name}
            onClick={() => {
                onNavigationSelection(currentItem)
                addQueryToNewLink(currentItem?.href)
            }}
            className={`inline-flex w-full !h-[90%] flex-col items-center justify-center py-3 gap-0 hover:bg-secondary group relative ${
                getValueFromUrl(pathname) === getValueFromUrl(currentItem.href) && 'bg-secondary'
            }`}
        >
            <div className='flex flex-col flex-1 items-center'>
                {currentItem.icon}
                <span className='text-secondary-foreground text-[10px] justify-between flex group-hover:text-primary-foreground'>
                    {currentItem?.name}
                </span>
            </div>
        </Button>
    )

    const renderDrawerButton = (currentItem: ILeftSidebarItems) => (
        <SheetClose key={currentItem.href} asChild className='w-full'>
            <Button
                className={`flex items-center justify-start focus:outline-none focus:ring-0 ${
                    getValueFromUrl(pathname) === getValueFromUrl(currentItem.href) && 'bg-secondary'
                }`}
                type='button'
                onClick={() => onNavigationSelection(currentItem)}
                variant='ghost'
            >
                <span className='mr-3'>{currentItem.icon}</span>
                {currentItem.name || ''}
            </Button>
        </SheetClose>
    )

    return (
        <div className='fixed bottom-0 flex inset-x-0 lg:hidden z-30 !gradient_bg border-t shadow-lg text-primary-foreground'>
            <div
                className='!h-full w-full px-1 grid gap-x-1 font-medium items-center'
                style={{
                    gridTemplateColumns: `repeat(${needsDrawer ? bottomNavigationItems.length + 1 : bottomNavigationItems.length}, 1fr)`
                }}
            >
                {bottomNavigationItems.map(renderNavigationButton)}

                {needsDrawer && (
                    <Sheet>
                        <SheetTrigger className='cursor-pointer' asChild>
                            <div
                                className={`inline-flex w-full flex-col items-center justify-center px-5 py-3 hover:bg-secondary rounded-md h-[90%] group ${
                                    isMoreActive ? 'bg-secondary' : ''
                                }`}
                            >
                                <EllipsisVertical className='text-2xl' />
                                <span className='text-[10px] text-secondary-foreground group-hover:text-primary-foreground'>
                                    More
                                </span>
                            </div>
                        </SheetTrigger>
                        <SheetContent className='z-50 p-0 gradient_bg'>
                            <SheetTitle className='sr-only'>More Options</SheetTitle>
                            <SheetHeader className='h-16 p-4 border-b'>
                                <div className='flex gap-x-2'>
                                    <h5>More</h5>
                                </div>
                            </SheetHeader>
                            <div className='flex flex-col p-4 gap-y-1'>{drawerItems.map(renderDrawerButton)}</div>
                        </SheetContent>
                    </Sheet>
                )}
            </div>
        </div>
    )
}

export default MobileNavigation
